/**
 * borrowedQuestionGate.js — Whole-output gate for interviewer-question residue.
 *
 * Sibling of F-116 (outputGate.js). Different fail path: one corrective regen, then
 * failGeneration — never Flick, never raw answers dressed as Whole.
 *
 * A sentence is dirty when it is BOTH:
 *   1. led by a closed yes/no staple (Absolutely / Yes / Yeah / I did / I do), AND
 *   2. a paraphrase of a Pip/interviewer turn (content-word overlap, or a 15-word
 *      for-word span).
 *
 * A teller repeating their own answer is not this class — the staple-only answer
 * "Absolutely. I was blessed." does not restate the question.
 *
 * Detector is code, not a grader. No lookbehind (LOOKBEHIND-BOOT-1).
 */

export const YES_NO_STAPLE_RE = /^(absolutely|yes|yeah|i did|i do)\b/i;
const STAPLE_STRIP_RE = /^(absolutely|yes|yeah|i did|i do)\b[,.]?\s*/i;

const STOP = new Set((
  'the a an and of to in it that this those these is was are were am '
  + 'i you your my me he she we they them his her our their on at so for with as but or if then there here '
  + 'what how just really very much some any all no not yes yeah oh well its did do does have has had '
  + 'can could would should will be been being like from about when where who whom whose why which '
  + 'up out off into over after before also too still even absolutely'
).split(' '));

const VERBATIM_SPAN = 15;
const MIN_QUESTION_CONTENT = 2;
const OVERLAP_COVERAGE = 0.7;
const MAX_EXTRA_CONTENT = 2;

function splitSentences(text) {
  const src = String(text == null ? '' : text);
  if (!src.trim()) return [];
  return (src.match(/[^.!?]+[.!?]*/g) || [src]).map((s) => s.trim()).filter(Boolean);
}

function words(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9'\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

function stem(w) {
  return w.replace(/(ing|ed|es|s)$/, '') || w;
}

function contentWords(s) {
  return words(s)
    .filter((w) => !STOP.has(w))
    .map(stem)
    .filter((w) => w.length > 2);
}

function hasStaple(sentence) {
  return YES_NO_STAPLE_RE.test(String(sentence || '').trim());
}

function stripStaple(sentence) {
  return String(sentence || '').trim().replace(STAPLE_STRIP_RE, '').trim();
}

function hasVerbatimSpan(sentence, turn, n = VERBATIM_SPAN) {
  const a = words(sentence);
  const b = words(turn);
  if (a.length < n || b.length < n) return false;
  const hay = ` ${a.join(' ')} `;
  for (let i = 0; i + n <= b.length; i++) {
    const span = ` ${b.slice(i, i + n).join(' ')} `;
    if (hay.includes(span)) return true;
  }
  return false;
}

function isParaphraseOf(sentence, turn) {
  const remainder = stripStaple(sentence);
  if (!remainder) return false;
  if (hasVerbatimSpan(remainder, turn) || hasVerbatimSpan(sentence, turn)) return true;
  const sc = contentWords(remainder);
  if (!sc.length) return false;
  const units = splitSentences(turn);
  if (!units.length) units.push(String(turn || ''));
  for (const unit of units) {
    const qc = contentWords(unit);
    if (qc.length < MIN_QUESTION_CONTENT) continue;
    const have = new Set(sc);
    const inter = qc.filter((w) => have.has(w));
    const cov = inter.length / qc.length;
    const extra = sc.filter((w) => !qc.includes(w)).length;
    if (cov >= OVERLAP_COVERAGE && extra <= MAX_EXTRA_CONTENT) return true;
  }
  return false;
}

/**
 * Return the Whole sentences that restate an interviewer turn plus a yes/no staple.
 * `interviewerTurns` is an array of Pip turn strings (typed assistant or voice ai).
 */
export function findBorrowedQuestionSentences(story, interviewerTurns) {
  const turns = (Array.isArray(interviewerTurns) ? interviewerTurns : [])
    .map((t) => (t == null ? '' : String(t)))
    .filter((t) => t.trim());
  if (!turns.length) return [];
  const dirty = [];
  for (const sent of splitSentences(story)) {
    if (!hasStaple(sent)) continue;
    if (turns.some((turn) => isParaphraseOf(sent, turn))) dirty.push(sent);
  }
  return dirty;
}

/**
 * Composite gate. Shape matches outputGate.gateStoryOutput: { ok, reasons, … }.
 * Does NOT fall back to Flick/raw — the caller fails honestly.
 */
export function gateBorrowedQuestions(story, interviewerTurns) {
  const dirtySentences = findBorrowedQuestionSentences(story, interviewerTurns);
  return {
    ok: dirtySentences.length === 0,
    reasons: dirtySentences.length ? ['borrowed-question'] : [],
    dirtySentences,
  };
}

/**
 * Corrective regen directive appended to wholePrompt (same composition as
 * correctiveRegenDirective). Names the dirty sentences so the rewrite can find them.
 */
export function borrowedQuestionRegenDirective({ dirtySentences = [] } = {}) {
  if (!dirtySentences.length) return '';
  const named = dirtySentences.map((s) => `"${String(s).trim()}"`).join(' ');
  return (
    '\n\nCRITICAL OUTPUT RULE: These sentences restated the interviewer\'s question with a yes/no staple '
    + '(Absolutely / Yes / Yeah / I did) and must not appear in the finished story: '
    + named
    + ' Rewrite them from the teller\'s own answers. Do not keep the question-plus-staple. '
    + 'Output one title line, then the story.'
  );
}
